import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DELIVERED_SHOWN_DAYS, daysBefore, labDayOf, type JobOrder } from '@/domain';
import { useJobOrderRepository } from '../container';
import { liveRefetchInterval } from '../live';
import { EVERY_PAGE_SIZE, everyPage } from '../paging';
import { keys } from '../query-keys';
import type {
  DeletionCommand,
  DispatchCommand,
  JobOrderFilter,
  MarkDeliveredCommand,
  MarkReviewedCommand,
  SaveReceiptCommand,
  SetDatesCommand,
  UnmarkReceivedCommand,
  UpdateJobOrderCommand,
  UpdateShippingCommand,
} from '../ports/JobOrderRepository';

/**
 * The order lifecycle: Orders, Payments and Shipping. Sections 5.2 and 7.5.
 *
 * None of these is optimistic. Section 8.3 permits optimism on three
 * interactions only, and payment, hold and dispatch are not among them:
 * showing an order as dispatched before the server agreed is how a case ends
 * up believed shipped while it is still on a shelf.
 */

export function useJobOrders(filter: JobOrderFilter) {
  const repo = useJobOrderRepository();
  return useQuery({
    queryKey: [...keys.orders.all, filter],
    queryFn: () => repo.list(filter),
    staleTime: 15_000,
    refetchInterval: liveRefetchInterval,
    // The page in hand stays on screen while another page or another order is
    // fetched, so the table does not blank under the reader. It matters more
    // since newest first is asked of the server (request of 5 October 2026,
    // section 2): choosing another order is now a request.
    placeholderData: (previous) => previous,
  });
}

/**
 * Every order in hand and every one delivered inside the shipping screen's
 * fortnight, all pages of it. The shipping desk and the logistics column read
 * this: they show everything at once, grouped, rather than a page. They share
 * one cache entry. Section 25 of the review of 2 October 2026.
 */
export function useOrdersInHand() {
  const repo = useJobOrderRepository();
  const deliveredSince = daysBefore(labDayOf(Date.now()), DELIVERED_SHOWN_DAYS - 1);
  return useQuery({
    queryKey: [...keys.orders.all, 'in-hand', deliveredSince],
    queryFn: () =>
      everyPage((page) =>
        repo.list({ includeDispatched: true, deliveredSince, page, pageSize: EVERY_PAGE_SIZE }),
      ),
    staleTime: 15_000,
    refetchInterval: liveRefetchInterval,
  });
}

export function useJobOrder(jobOrderNo: string | undefined) {
  const repo = useJobOrderRepository();
  return useQuery({
    queryKey: keys.orders.byNo(jobOrderNo ?? ''),
    queryFn: () => repo.byNo(jobOrderNo as string),
    enabled: jobOrderNo !== undefined,
    refetchInterval: liveRefetchInterval,
  });
}

function useOrderMutation<Input>(run: (input: Input) => Promise<JobOrder>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: keys.orders.all });
      void queryClient.invalidateQueries({ queryKey: keys.kits.all });
      void queryClient.invalidateQueries({ queryKey: keys.board() });
      void queryClient.invalidateQueries({ queryKey: keys.notifications });
      // The dates are printed on the sheet.
      void queryClient.invalidateQueries({ queryKey: keys.printAll });
      // Every one of these writes a row into the case's history.
      void queryClient.invalidateQueries({ queryKey: ['activity'] });
      // The case record and the archive row read the order: a dispatch puts a case
      // in the archive, and a received mark fills its Received column.
      void queryClient.invalidateQueries({ queryKey: ['archive'] });
      // A doctor's and a patient's record count their cases in hand, and a
      // profile lists them: a dispatch or a hold shows there too. The request
      // of 6 October 2026 (second), items 4 and 5.
      void queryClient.invalidateQueries({ queryKey: keys.doctors.all });
      void queryClient.invalidateQueries({ queryKey: keys.patients.all });
    },
  });
}

/**
 * The cases in hand, counted, for the admin's figures. The lab's request of
 * 17 October 2026, items 3 and 5. Asked only where the figures are drawn.
 */
export function useJobOrderCounts(enabled = true) {
  const repo = useJobOrderRepository();
  return useQuery({
    queryKey: keys.orders.counts(),
    queryFn: () => repo.counts(),
    enabled,
    staleTime: 15_000,
    refetchInterval: liveRefetchInterval,
  });
}

/** Released to be shipped unpaid. The lab's request of 17 October 2026, item 8. */
export function useReleaseCase() {
  const repo = useJobOrderRepository();
  return useOrderMutation((jobOrderNo: string) => repo.release(jobOrderNo));
}

/** The release taken back: its Undo, and later from Released. */
export function useTakeBackRelease() {
  const repo = useJobOrderRepository();
  return useOrderMutation((jobOrderNo: string) => repo.takeBackRelease(jobOrderNo));
}

export function useMarkPaid() {
  const repo = useJobOrderRepository();
  return useOrderMutation((jobOrderNo: string) => repo.markPaid(jobOrderNo));
}

/** Undo for Mark paid: the case goes back to be paid. Request of 5 October 2026. */
export function useUndoPayment() {
  const repo = useJobOrderRepository();
  return useOrderMutation((jobOrderNo: string) => repo.undoPayment(jobOrderNo));
}

/**
 * What there is to review on a case: each change with who made it, when, and
 * every field before and after, and the review that stands on it. Read when the
 * changes sheet opens, and again whenever an order changes, so the `since` it
 * hands to Mark as reviewed is the one on screen. The request of
 * 5 October 2026 (third), section 3.
 */
export function useAccountsReviewDetail(jobOrderNo: string | null) {
  const repo = useJobOrderRepository();
  return useQuery({
    queryKey: keys.orders.accountsReview(jobOrderNo ?? ''),
    queryFn: () => repo.accountsReviewDetail(jobOrderNo as string),
    enabled: jobOrderNo !== null,
  });
}

/**
 * The cases waiting for Accounts that changed nothing of the bill, for
 * management to read before clearing them. The request of 7 October 2026,
 * section 5. Asked only of somebody who may clear them.
 */
export function useReviewCleanup(enabled: boolean) {
  const repo = useJobOrderRepository();
  return useQuery({
    queryKey: keys.orders.reviewCleanup(),
    queryFn: () => repo.reviewCleanup(),
    enabled,
  });
}

/** Clears them. Every list of cases is read again after it, the Changed list among them. */
export function useClearReviewCleanup() {
  const repo = useJobOrderRepository();
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => repo.clearReviewCleanup(),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.orders.all }),
  });
}

/**
 * Accounts have looked at what changed on a case since it was priced. `seen` is
 * the detail's `since`: the server refuses when the case was edited again after
 * the changes the screen showed.
 */
export function useAccountsReviewed() {
  const repo = useJobOrderRepository();
  return useOrderMutation((command: MarkReviewedCommand) =>
    repo.accountsReviewed(command.jobOrderNo, command.seen ?? undefined),
  );
}

/**
 * Takes a review back: the Undo after Mark as reviewed, and Mark as not reviewed
 * on a reviewed case. The case goes back to the Changed list. The request of
 * 5 October 2026 (third), section 3.
 */
export function useAccountsUnreview() {
  const repo = useJobOrderRepository();
  return useOrderMutation((jobOrderNo: string) => repo.accountsUnreview(jobOrderNo));
}

/**
 * A case's receipt for Accounts: what each line is, its teeth, its price and
 * the total, read before the case is marked paid. Request of 5 October 2026.
 */
export function useReceipt(jobOrderNo: string | null) {
  const repo = useJobOrderRepository();
  return useQuery({
    queryKey: keys.orders.receipt(jobOrderNo ?? ''),
    queryFn: () => repo.receipt(jobOrderNo as string),
    enabled: jobOrderNo !== null,
  });
}

/** New prices for one case only. The case's value moves with them, so the lists refetch. */
export function useSaveReceipt() {
  const repo = useJobOrderRepository();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (command: SaveReceiptCommand) => repo.saveReceipt(command),
    onSuccess: (receipt) => {
      queryClient.setQueryData(keys.orders.receipt(receipt.jobOrderNo), receipt);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: keys.orders.all });
      void queryClient.invalidateQueries({ queryKey: ['archive'] });
      void queryClient.invalidateQueries({ queryKey: ['activity'] });
    },
  });
}

export function useSetOrderDates() {
  const repo = useJobOrderRepository();
  return useOrderMutation((command: SetDatesCommand) => repo.setDates(command));
}

/**
 * Editing a case after intake. Everything that shows a case may show what
 * changed, the lists, the job view, the record and the board, so all of it is
 * refetched: an edit is rare, and a stale field on a bench is not.
 */
export function useUpdateJobOrder() {
  const repo = useJobOrderRepository();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (command: UpdateJobOrderCommand) => repo.updateJobOrder(command),
    onSettled: () => {
      void queryClient.invalidateQueries();
    },
  });
}

export function useHoldOrder() {
  const repo = useJobOrderRepository();
  return useOrderMutation((input: { jobOrderNo: string; reason: string }) =>
    repo.hold(input.jobOrderNo, input.reason),
  );
}

export function useReleaseHold() {
  const repo = useJobOrderRepository();
  return useOrderMutation((jobOrderNo: string) => repo.releaseHold(jobOrderNo));
}

export function useDispatchOrder() {
  const repo = useJobOrderRepository();
  return useOrderMutation((command: DispatchCommand) => repo.dispatch(command));
}

/** The waybill code the morning after, or a correction to any of a case's shipping. */
export function useUpdateShipping() {
  const repo = useJobOrderRepository();
  return useOrderMutation((command: UpdateShippingCommand) => repo.updateShipping(command));
}

/**
 * Marks a case received by the customer. The order it answers carries who
 * marked it and when, and the rowVersion `useUnmarkReceived` sends back. The
 * server refuses a case that is already marked. The request of 5 October 2026
 * (third), section 11.
 */
export function useMarkDelivered() {
  const repo = useJobOrderRepository();
  return useOrderMutation((command: MarkDeliveredCommand) => repo.markDelivered(command));
}

/**
 * Takes a received mark back: the Undo after Mark received. The case reads
 * Shipped again, and who signed and who marked it go with the mark. An edit of
 * the shipping with `deliveredAt` null, which is also how the mark is corrected
 * later from the shipping dialog, so there is one way to un-mark and the server
 * keeps one rule for it.
 */
export function useUnmarkReceived() {
  const repo = useJobOrderRepository();
  return useOrderMutation((command: UnmarkReceivedCommand) =>
    repo.updateShipping({ ...command, changes: { deliveredAt: null } }),
  );
}

/**
 * The four deletion commands. Each refetches everything: a case deleted leaves
 * Orders, a stage, the board, the reports and the archive at once, and restored
 * it comes back to all of them, and a list still showing it would invite
 * somebody to work a case that is gone.
 */
function useDeletionMutation<Input, Result = JobOrder>(run: (input: Input) => Promise<Result>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSettled: () => {
      void queryClient.invalidateQueries();
    },
  });
}

export function useRequestDeletion() {
  const repo = useJobOrderRepository();
  return useDeletionMutation((command: DeletionCommand & { reason: string }) =>
    repo.requestDeletion(command),
  );
}

export function useWithdrawDeletion() {
  const repo = useJobOrderRepository();
  return useDeletionMutation((command: DeletionCommand) => repo.withdrawDeletion(command));
}

export function useApproveDeletion() {
  const repo = useJobOrderRepository();
  return useDeletionMutation((command: DeletionCommand) => repo.approveDeletion(command));
}

export function useRestoreCase() {
  const repo = useJobOrderRepository();
  return useDeletionMutation((command: DeletionCommand) => repo.restoreCase(command));
}

/**
 * Deleting a case for good, from the archive's Deleted tab. Request of
 * 24 September 2026. Nothing comes back but a 204, so the answer is an absence:
 * every list refetches, and the case is on none of them, the Deleted tab and
 * its own record included.
 */
export function usePurgeCase() {
  const repo = useJobOrderRepository();
  return useDeletionMutation((command: DeletionCommand) => repo.purgeCase(command));
}
